import { useEffect, useState } from 'react'

import { Button } from '@canyon/ui/Button'
import { Checkbox } from '@canyon/ui/Checkbox'
import { Form, FormControl, FormField, FormItem, FormMessage } from '@canyon/ui/Form'
import { Input } from '@canyon/ui/Input'
import { Label } from '@canyon/ui/Label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@canyon/ui/Select'
import { SubmitHandler, UseFormReturn } from 'react-hook-form'

import {
  JobListingDatePostedEnum,
  JobListingEducationLevelEnum,
  JobListingEmploymentTypeEnum,
  JobListingExperienceLevelEnum,
  JobListingPayPeriodEnum,
  JobListingWorkArrangementEnum,
} from '@gql/graphql'

import {
  datePostedOptions,
  educationLevelOptions,
  employmentTypeOptions,
  experienceLevelOptions,
  payPeriodOptions,
  workArrangementOptions,
} from '@/constants/jobListings'
import { TJobSearchPreferencesForm } from '@/forms/types'
import { LocationSearchInput } from '@/modules/job-search/components/LocationSearchInput'

export const JOB_SEARCH_PREFERENCES_FORM_ID = 'job-search-preferences-form'

interface IJobSearchPreferencesFormProps {
  form: UseFormReturn<TJobSearchPreferencesForm>
  isSubmitting?: boolean
  onSubmit: SubmitHandler<TJobSearchPreferencesForm>
}

const EMPLOYMENT_TYPES = Object.entries(employmentTypeOptions).map(([value, label]) => ({
  label,
  value: value as JobListingEmploymentTypeEnum,
}))

const EXPERIENCE_LEVELS = Object.entries(experienceLevelOptions).map(([value, label]) => ({
  label,
  value: value as JobListingExperienceLevelEnum,
}))

const WORK_ARRANGEMENTS = Object.entries(workArrangementOptions).map(([value, label]) => ({
  label,
  value: value as JobListingWorkArrangementEnum,
}))

const DATE_POSTED_OPTIONS = Object.entries(datePostedOptions).map(([value, label]) => ({
  label,
  value: value as JobListingDatePostedEnum,
}))

const EDUCATION_LEVELS = Object.entries(educationLevelOptions).map(([value, label]) => ({
  label,
  value: value as JobListingEducationLevelEnum,
}))

const PAY_PERIODS = Object.entries(payPeriodOptions).map(([value, label]) => ({
  label,
  value: value as JobListingPayPeriodEnum,
}))

export const JobSearchPreferencesForm = ({
  form,
  isSubmitting = false,
  onSubmit,
}: IJobSearchPreferencesFormProps) => {
  const isDirty = form.formState.isDirty
  const keywordsValue = form.watch('jobSearchPreferences.keywords')
  const [keywordsInput, setKeywordsInput] = useState('')

  useEffect(() => {
    setKeywordsInput(keywordsValue?.join(', ') || '')
  }, [keywordsValue])

  return (
    <Form {...form}>
      <form
        className="space-y-4"
        id={JOB_SEARCH_PREFERENCES_FORM_ID}
        onSubmit={form.handleSubmit(onSubmit)}
      >
        <div
          className="space-y-6 max-h-[400px] overflow-y-scroll pr-2 scrollbar scrollbar-thumb-muted-foreground/30
            scrollbar-track-background scrollbar-thin"
        >
          <div className="space-y-3">
            <Label className="text-sm font-semibold">Keywords</Label>
            <FormField
              control={form.control}
              name="jobSearchPreferences.keywords"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <Input
                      onBlur={() => {
                        const value = keywordsInput
                          .split(',')
                          .map((s) => s.trim())
                          .filter((s) => s.length > 0)
                        field.onChange(value.length > 0 ? value : undefined)
                      }}
                      onChange={(e) => setKeywordsInput(e.target.value)}
                      placeholder="e.g., React, TypeScript, Node.js"
                      value={keywordsInput}
                    />
                  </FormControl>
                  <p className="text-xs text-muted-foreground">
                    Separate multiple keywords with commas
                  </p>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <div className="space-y-3">
            <Label className="text-sm font-semibold">Locations</Label>
            <FormField
              control={form.control}
              name="jobSearchPreferences.locations"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <LocationSearchInput
                      maxLocations={3}
                      setValue={(locations) =>
                        field.onChange(locations.length > 0 ? locations : undefined)
                      }
                      showRadius={false}
                      value={field.value || []}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <div className="space-y-3">
            <Label className="text-sm font-semibold">Employment Types</Label>
            <FormField
              control={form.control}
              name="jobSearchPreferences.employmentTypes"
              render={({ field }) => (
                <FormItem>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {EMPLOYMENT_TYPES.map((employmentType) => (
                      <div className="flex items-center space-x-2" key={employmentType.value}>
                        <Checkbox
                          checked={field.value?.includes(employmentType.value) || false}
                          id={`employment-${employmentType.value}`}
                          onCheckedChange={(checked) => {
                            const current = field.value || []
                            const updated = checked
                              ? [...current, employmentType.value]
                              : current.filter((v) => v !== employmentType.value)
                            field.onChange(updated)
                          }}
                        />
                        <Label className="text-sm" htmlFor={`employment-${employmentType.value}`}>
                          {employmentType.label}
                        </Label>
                      </div>
                    ))}
                  </div>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <div className="space-y-3">
            <Label className="text-sm font-semibold">Experience Levels</Label>
            <FormField
              control={form.control}
              name="jobSearchPreferences.experienceLevels"
              render={({ field }) => (
                <FormItem>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {EXPERIENCE_LEVELS.map((experienceLevel) => (
                      <div className="flex items-center space-x-2" key={experienceLevel.value}>
                        <Checkbox
                          checked={field.value?.includes(experienceLevel.value) || false}
                          id={`experience-${experienceLevel.value}`}
                          onCheckedChange={(checked) => {
                            const current = field.value || []
                            const updated = checked
                              ? [...current, experienceLevel.value]
                              : current.filter((v) => v !== experienceLevel.value)
                            field.onChange(updated)
                          }}
                        />
                        <Label className="text-sm" htmlFor={`experience-${experienceLevel.value}`}>
                          {experienceLevel.label}
                        </Label>
                      </div>
                    ))}
                  </div>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <div className="space-y-3">
            <Label className="text-sm font-semibold">Work Arrangements</Label>
            <FormField
              control={form.control}
              name="jobSearchPreferences.workArrangements"
              render={({ field }) => (
                <FormItem>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {WORK_ARRANGEMENTS.map((workArrangement) => (
                      <div className="flex items-center space-x-2" key={workArrangement.value}>
                        <Checkbox
                          checked={field.value?.includes(workArrangement.value) || false}
                          id={`work-${workArrangement.value}`}
                          onCheckedChange={(checked) => {
                            const current = field.value || []
                            const updated = checked
                              ? [...current, workArrangement.value]
                              : current.filter((v) => v !== workArrangement.value)
                            field.onChange(updated)
                          }}
                        />
                        <Label className="text-sm" htmlFor={`work-${workArrangement.value}`}>
                          {workArrangement.label}
                        </Label>
                      </div>
                    ))}
                  </div>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <div className="space-y-3">
            <Label className="text-sm font-semibold">Date Posted</Label>
            <FormField
              control={form.control}
              name="jobSearchPreferences.datePosted"
              render={({ field }) => (
                <FormItem>
                  <Select
                    onValueChange={(value) =>
                      field.onChange(value ? (value as JobListingDatePostedEnum) : undefined)
                    }
                    value={field.value || ''}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select date posted filter" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {DATE_POSTED_OPTIONS.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <div className="space-y-3">
            <Label className="text-sm font-semibold">Education Requirement</Label>
            <FormField
              control={form.control}
              name="jobSearchPreferences.educationRequirement"
              render={({ field }) => (
                <FormItem>
                  <Select
                    onValueChange={(value) =>
                      field.onChange(value ? (value as JobListingEducationLevelEnum) : undefined)
                    }
                    value={field.value || ''}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select education requirement" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {EDUCATION_LEVELS.map((level) => (
                        <SelectItem key={level.value} value={level.value}>
                          {level.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <div className="space-y-3">
            <Label className="text-sm font-semibold">Minimum Salary</Label>
            <div className="space-y-2">
              <FormField
                control={form.control}
                name="jobSearchPreferences.salaryMin"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <Input
                        {...field}
                        onChange={(e) =>
                          field.onChange(e.target.value ? parseInt(e.target.value) : undefined)
                        }
                        placeholder="e.g., 50000"
                        type="number"
                        value={field.value?.toString() || ''}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="jobSearchPreferences.payPeriod"
                render={({ field }) => (
                  <FormItem>
                    <Select
                      onValueChange={(value) =>
                        field.onChange(value ? (value as JobListingPayPeriodEnum) : undefined)
                      }
                      value={field.value || JobListingPayPeriodEnum.Year}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Pay period" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {PAY_PERIODS.map((period) => (
                          <SelectItem key={period.value} value={period.value}>
                            {period.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </div>

          <div className="space-y-3">
            <Label className="text-sm font-semibold">Company Size</Label>
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-4">
              <FormField
                control={form.control}
                name="jobSearchPreferences.companyNumEmployeesMin"
                render={({ field }) => (
                  <FormItem className="flex-1">
                    <FormControl>
                      <Input
                        {...field}
                        onChange={(e) =>
                          field.onChange(e.target.value ? parseInt(e.target.value) : undefined)
                        }
                        placeholder="Min employees"
                        type="number"
                        value={field.value?.toString() || ''}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <span className="text-sm text-muted-foreground text-center sm:text-left">to</span>
              <FormField
                control={form.control}
                name="jobSearchPreferences.companyNumEmployeesMax"
                render={({ field }) => (
                  <FormItem className="flex-1">
                    <FormControl>
                      <Input
                        {...field}
                        onChange={(e) =>
                          field.onChange(e.target.value ? parseInt(e.target.value) : undefined)
                        }
                        placeholder="Max employees"
                        type="number"
                        value={field.value?.toString() || ''}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </div>

          <div className="flex items-center space-x-2">
            <FormField
              control={form.control}
              name="jobSearchPreferences.visaSponsorship"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center space-x-2 space-y-0">
                  <FormControl>
                    <Checkbox
                      checked={field.value || false}
                      id="visaSponsorship"
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                  <Label className="text-sm" htmlFor="visaSponsorship">
                    U.S. Visa sponsorship
                  </Label>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </div>

        {isDirty && (
          <div className="sticky bottom-0 pt-4 pb-2 mt-6 border-t-0.5 border-border">
            <Button
              className="w-full"
              disabled={isSubmitting}
              loading={isSubmitting}
              type="submit"
              variant="primary"
            >
              Apply Changes
            </Button>
          </div>
        )}
      </form>
    </Form>
  )
}
